/**
 * swipe-back.ts — 全域左緣滑動返回手勢(iOS Safari 標準方向)。
 *
 * boss 2026-06-14:從螢幕**最左邊邊緣往右滑** → 回上一頁。在 learn app init 掛一次,
 * 全域監聽 touch,觸發時呼 back-nav 的 `goBack()`。
 *
 * 判定(純幾何,抽成 `shouldArmEdgeSwipe` / `passesSwipeThreshold` 可單測):
 *   - **arm**:`touchstart` 起點 X ≤ `EDGE_PX`(24px)才武裝;否則整段忽略。
 *   - **觸發**:`touchend` 時向右位移 `dx > MIN_DX`(60px)且以水平為主
 *     (`|dx| > |dy| * H_RATIO`,H_RATIO=1.5)。
 *
 * 嚴格不干擾:
 *   - 只在「起點落在左緣」才 arm —— 垂直捲動 / 一般水平捲動內容起點通常不在最左 24px。
 *   - 桌機滑鼠不啟用(只綁 `touch*`)。
 *   - 遊戲畫面不啟用 —— 由呼叫端傳 `isActive()`(學習 DOM 顯示中才回 true),且事件
 *     起點若落在 `[data-no-swipe-back]`(canvas / slider / 水平捲動容器標記)子樹內則放棄。
 *   - 多指(pinch / 二指捲動)不 arm。
 *
 * 跟手 peek(加分):arm 後 `touchmove` 即時把 app 容器 `translateX` 跟著手指(夾在
 * 0..PEEK_MAX),放開未過閾值則彈回。放開過閾值 → goBack()(由換頁負責清掉 transform)。
 *
 * ⚠️ 純導覽,不碰任何學習/遊戲數值。
 */

import { goBack } from './back-nav';

/** 起點需落在的左緣寬度(px)。 */
export const EDGE_PX = 24;
/** 觸發返回所需的最小向右位移(px)。 */
export const MIN_DX = 60;
/** 水平為主的比例門檻:|dx| 需 > |dy| * H_RATIO。 */
export const H_RATIO = 1.5;
/** 跟手 peek 的最大位移(px),避免整頁被拖太遠。 */
export const PEEK_MAX = 120;

/** 是否該武裝邊緣滑動 —— 起點落在左緣且單指。 */
export function shouldArmEdgeSwipe(startX: number, touchCount: number): boolean {
  return touchCount === 1 && startX >= 0 && startX <= EDGE_PX;
}

/**
 * 位移是否通過「向右 + 水平為主」門檻。
 * @param dx 水平位移(向右為正)。
 * @param dy 垂直位移。
 */
export function passesSwipeThreshold(dx: number, dy: number): boolean {
  return dx > MIN_DX && Math.abs(dx) > Math.abs(dy) * H_RATIO;
}

/** mountSwipeBack 設定。 */
export interface SwipeBackOptions {
  /**
   * 學習 DOM 是否正顯示中(= 可啟用手勢)。遊戲模式 / 非學習畫面回 false,手勢直接放棄,
   * 確保「遊戲畫面不觸發滑動返回」(硬規則)。
   */
  isActive: () => boolean;
  /**
   * 跟手 peek 用的目標容器(通常 = learnRoot)。提供時 arm 後即時 translateX 跟手,放開
   * 彈回 / 清除;省略則只做「放開過閾值就返回」(無 peek 視覺)。
   */
  peekTarget?: HTMLElement;
  /**
   * 觸發返回的動作(預設 = back-nav 的 `goBack`)。可注入給測試,生產不需傳。
   */
  onBack?: () => void;
}

/** touchstart 起點若落在這些選擇器的子樹內,放棄手勢(避免干擾 slider / canvas / 橫向捲動)。 */
const NO_SWIPE_SELECTOR =
  'canvas, input[type="range"], [data-no-swipe-back], .efp-no-swipe';

/**
 * 掛全域左緣滑動返回。回傳卸載函式(移除 listener + 清乾淨 peek transform)。
 * 在 learn app init 掛一次即可。
 */
export function mountSwipeBack(opts: SwipeBackOptions): () => void {
  const triggerBack = opts.onBack ?? goBack;
  let armed = false;
  let startX = 0;
  let startY = 0;
  let peeking = false;

  const clearPeek = (): void => {
    if (opts.peekTarget && peeking) {
      opts.peekTarget.style.transform = '';
      opts.peekTarget.style.transition = '';
    }
    peeking = false;
  };

  const onStart = (e: TouchEvent): void => {
    armed = false;
    if (!opts.isActive()) return;
    if (e.touches.length !== 1) return; // 多指放棄
    const t = e.touches[0];
    if (!shouldArmEdgeSwipe(t.clientX, e.touches.length)) return;
    // 起點落在不可滑區(canvas / slider / 橫向捲動)→ 放棄。
    const target = e.target as Element | null;
    if (target && target.closest && target.closest(NO_SWIPE_SELECTOR)) return;
    armed = true;
    startX = t.clientX;
    startY = t.clientY;
  };

  const onMove = (e: TouchEvent): void => {
    if (!armed) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    // 明顯偏垂直 → 取消武裝(讓位給捲動)。
    if (Math.abs(dy) > Math.abs(dx) * H_RATIO && Math.abs(dy) > 12) {
      armed = false;
      clearPeek();
      return;
    }
    // 跟手 peek:夾在 0..PEEK_MAX。
    if (opts.peekTarget && dx > 0) {
      peeking = true;
      const offset = Math.min(dx, PEEK_MAX);
      opts.peekTarget.style.transition = 'none';
      opts.peekTarget.style.transform = `translateX(${offset}px)`;
    }
  };

  const onEnd = (e: TouchEvent): void => {
    if (!armed) {
      clearPeek();
      return;
    }
    armed = false;
    const t = e.changedTouches[0];
    const dx = t ? t.clientX - startX : 0;
    const dy = t ? t.clientY - startY : 0;
    clearPeek();
    if (passesSwipeThreshold(dx, dy)) {
      triggerBack();
    }
  };

  const onCancel = (): void => {
    armed = false;
    clearPeek();
  };

  // passive:onMove 寫 transform 不需 preventDefault,維持捲動流暢。
  document.addEventListener('touchstart', onStart, { passive: true });
  document.addEventListener('touchmove', onMove, { passive: true });
  document.addEventListener('touchend', onEnd, { passive: true });
  document.addEventListener('touchcancel', onCancel, { passive: true });

  return () => {
    document.removeEventListener('touchstart', onStart);
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
    document.removeEventListener('touchcancel', onCancel);
    clearPeek();
  };
}
